// Payment terms: types, a `use…()` hook and a picker every module can drop into a form.
//
// Typed as a number of days, terms could not say "end of month" or "2% within 10 days", and every
// card spelt them on its own. The list is agreed once under Settings; cards and documents pick a
// code from it here, and the server turns the code into dates.
import { useApi } from "@keel/core";
import { Select } from "../Select";
import { Field } from "../ui";

export interface PaymentTerm {
  id: string;
  code: string;
  name: string;
  /** Days from the counting start to the due date; 0 is payment on the invoice date. */
  dueDays: number;
  /** Early-payment discount; 0 means none. */
  discountPct: number;
  /** How many days the discount is on offer, counted the same way as `dueDays`. */
  discountDays: number;
  /** Counting starts at the end of the invoice month instead of the invoice date. */
  endOfMonth: boolean;
  /** What a new card is put on when nobody picks; exactly one term holds it. */
  isDefault: boolean;
  /** Retired terms stay on the cards and documents that already name them. */
  isActive: boolean;
}

/** What `GET /setup/payment-terms/due-date` answers for one invoice date. */
export interface PaymentTermDates {
  dueDate: string;
  discountDate: string | null;
  discountPct: number;
}

/** The tenant's terms — current ones, or all of them for the settings page. */
export function usePaymentTerms(includeInactive = false) {
  return useApi<PaymentTerm[]>(`/setup/payment-terms${includeInactive ? "?includeInactive=true" : ""}`);
}

/** The terms in words, the way a person would say them: "End of month + 30 days · 2% within 10 days". */
export function describePaymentTerm(
  t: Pick<PaymentTerm, "dueDays" | "discountPct" | "discountDays" | "endOfMonth">,
) {
  const due = t.endOfMonth
    ? t.dueDays === 0
      ? "End of the invoice month"
      : `End of month + ${t.dueDays} days`
    : t.dueDays === 0
      ? "Due on the invoice date"
      : `Net ${t.dueDays} days`;
  const discount = t.discountPct > 0 ? ` · ${t.discountPct}% within ${t.discountDays} days` : "";
  return `${due}${discount}`;
}

export interface PaymentTermsSelectProps {
  /** The chosen code, or null for "nothing agreed" (the company default applies). */
  value: string | null | undefined;
  onChange: (code: string | null) => void;
  /** Allow no terms at all, with this label. */
  emptyLabel?: string;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * The bare control. Retired terms are not offered, except the one already on the card: a card on
 * terms that vanished from the dropdown would look like a card on no terms at all.
 */
export function PaymentTermsSelect({
  value,
  onChange,
  emptyLabel,
  disabled,
  placeholder = "Choose payment terms…",
}: PaymentTermsSelectProps) {
  const terms = usePaymentTerms(true).data ?? [];
  const offered = terms.filter((t) => t.isActive || t.code === value);
  return (
    <Select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      placeholder={placeholder}
    >
      {emptyLabel && <option value="">{emptyLabel}</option>}
      {offered.map((t) => (
        <option key={t.code} value={t.code}>
          {t.code} · {t.name}
          {t.isActive ? "" : " (retired)"}
        </option>
      ))}
    </Select>
  );
}

/** The same control with its label, and a hint that says what the chosen terms mean in days. */
export function PaymentTermsField({
  label,
  hint,
  ...rest
}: PaymentTermsSelectProps & { label: string; hint?: string }) {
  const terms = usePaymentTerms(true).data ?? [];
  const chosen = terms.find((t) => t.code === rest.value);
  return (
    <Field label={label} hint={chosen ? describePaymentTerm(chosen) : hint}>
      <PaymentTermsSelect {...rest} />
    </Field>
  );
}
